/**
 * Photo thumb with QA re-file / delete controls on the tile itself.
 *
 * Photo store mutations apply immediately (not batched into a report
 * revision). Callers own the FilingPicker placement so a single open picker
 * can sit under whichever tile is active.
 */

import type { ReactNode } from 'react';
import type { PhotoQuarterTurn, StoredPhotoManifest } from '@cer-autofill/shared';
import { PhotoThumb } from './PhotoThumb.js';

/** JPEG and PNG only. PDFs and WebP have no rotate control. */
export function canRotatePhoto(mimeType: string | undefined): boolean {
  if (!mimeType) return false;
  const mime = mimeType.toLowerCase();
  return mime === 'image/jpeg' || mime === 'image/jpg' || mime === 'image/png';
}

export function EditablePhotoTile({
  manifest,
  caption,
  canManage,
  busy,
  refiling,
  onOpen,
  onToggleRefile,
  onRotate,
  onDelete,
  filingPicker,
}: {
  manifest: StoredPhotoManifest;
  caption?: string;
  canManage: boolean;
  busy: boolean;
  refiling: boolean;
  onOpen: (blobUrl: string) => void;
  onToggleRefile: () => void;
  onRotate: (degrees: PhotoQuarterTurn) => void;
  onDelete: () => void;
  filingPicker?: ReactNode;
}) {
  return (
    <div className={`photo-tile${refiling ? ' photo-tile--refiling' : ''}`}>
      <PhotoThumb
        jobId={manifest.jobId}
        photoId={manifest.photoId}
        promptKey={manifest.promptKey}
        mimeType={manifest.mimeType}
        version={manifest.uploadedAt}
        {...(caption !== undefined ? { caption } : {})}
        onOpen={onOpen}
      />
      {canManage && (
        <div className="photo-tile__actions">
          {canRotatePhoto(manifest.mimeType) && (
            <>
              <button
                type="button"
                disabled={busy}
                aria-label="Rotate left"
                onClick={() => onRotate(270)}
              >
                Rotate ↺
              </button>
              <button
                type="button"
                disabled={busy}
                aria-label="Rotate right"
                onClick={() => onRotate(90)}
              >
                Rotate ↻
              </button>
            </>
          )}
          <button type="button" disabled={busy} onClick={onToggleRefile}>
            {refiling ? 'Cancel re-file' : 'Re-file'}
          </button>
          <button type="button" className="danger" disabled={busy} onClick={onDelete}>
            {busy ? 'Working…' : 'Delete'}
          </button>
        </div>
      )}
      {filingPicker}
    </div>
  );
}
